/**
 * Run-parameter form generated from the registry's param_ui declarations.
 *
 * Same rules as the previous operator form: month dropdown for `month`
 * controls (localized via Intl from the active language), sticky values
 * remembered per site (never period/year), fields hidden by `visible_when`,
 * `advanced` audience collapsed, `hidden` never rendered. `requireAll`
 * marks every operator field required — the wizard uses it because the end
 * of the pipeline (the Tagetik upload) needs the full identity.
 */
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { RunParam } from "../lib/api";
import { YEAR_MAX, YEAR_MIN } from "../lib/config";
import { isVisible, loadSticky, monthNames } from "../lib/params";
import { resolveText } from "../lib/tooltext";

export function ParamsForm({
  params,
  values,
  onChange,
  requireAll = false,
}: {
  params: RunParam[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  requireAll?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const months = useMemo(() => monthNames(i18n.language), [i18n.language]);

  // sticky prefill once per param set
  const paramKey = params.map((p) => p.name).join(",");
  useEffect(() => {
    const prefill: Record<string, string> = {};
    for (const p of params) {
      if (p.sticky && !(values[p.name] ?? "").trim()) {
        const remembered = loadSticky(p.name);
        if (remembered) prefill[p.name] = remembered;
      }
    }
    if (Object.keys(prefill).length) onChange({ ...prefill, ...values });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefill must run once per param set, not per keystroke
  }, [paramKey]);

  const visible = (p: RunParam) => isVisible(params, values, p);

  const set = (name: string, value: string) => onChange({ ...values, [name]: value });

  const field = (p: RunParam) => {
    const required = p.audience === "operator" && (p.required || requireAll);
    // Tool-declared text may be a plain string or a bilingual dict.
    const help = resolveText(p.help, i18n.language);
    const common = {
      label: resolveText(p.label, i18n.language) || p.name,
      value: values[p.name] ?? "",
      required,
      helperText: help || undefined,
      fullWidth: true,
      size: "small" as const,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(p.name, e.target.value),
    };
    if (p.control === "month") {
      return (
        <TextField {...common} select>
          <MenuItem value="">—</MenuItem>
          {months.map((label, i) => (
            <MenuItem key={label} value={String(i + 1)}>
              {String(i + 1).padStart(2, "0")} · {label}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (p.control === "choice") {
      return (
        <TextField {...common} select>
          <MenuItem value="">—</MenuItem>
          {p.choices.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (p.control === "year") {
      return (
        <TextField {...common} type="number" inputProps={{ min: YEAR_MIN, max: YEAR_MAX }} />
      );
    }
    if (p.control === "password") {
      return <TextField {...common} type="password" autoComplete="new-password" />;
    }
    if (p.control === "text") {
      return <TextField {...common} />;
    }
    // Unknown control declared by a plugged-in module: degrade to a text
    // input, but say so — silent degradation hides integration bugs.
    return (
      <Box>
        <TextField {...common} />
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          icon={<WarningAmberIcon />}
          label={t("params.unknownControl", { control: p.control })}
          sx={{ mt: 0.5 }}
        />
      </Box>
    );
  };

  // a "tabs" parameter is rendered by the page, above the upload zone
  const operator = params.filter(
    (p) => p.audience === "operator" && p.control !== "tabs" && visible(p),
  );
  const advanced = params.filter(
    (p) => p.audience === "advanced" && p.control !== "tabs" && visible(p),
  );

  return (
    <>
      <Grid container spacing={2}>
        {operator.map((p) => (
          <Grid item xs={12} sm={6} md={3} key={p.name}>
            {field(p)}
          </Grid>
        ))}
      </Grid>
      {advanced.length > 0 && (
        <Accordion variant="outlined" sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">{t("tools.advanced")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {advanced.map((p) => (
                <Grid item xs={12} sm={6} key={p.name}>
                  {field(p)}
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}
